import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { ConnectionStatus } from '../hooks/useSignalR';

export type ChatKind = 'private' | 'group';
export type DeliveryStatus = 'sending' | 'sent' | 'read' | 'failed';

export interface ChatParticipant {
  id: string;
  name: string;
  photoId?: string;
  photoUrl?: string;
  isOnline?: boolean;
}

export interface ChatConversation {
  id: string;
  conversationId?: number;
  kind: ChatKind;
  title: string;
  photoId?: string;
  photoUrl?: string;
  receiverId?: string;
  groupId?: number;
  memberCount?: number;
  lastMessage?: string;
  lastMessageAt?: string;
  unreadCount: number;
  isOnline?: boolean;
}

export interface ChatMessage {
  id: string;
  messageId?: number;
  conversationId?: number;
  kind: ChatKind;
  groupId?: number;
  senderId: string;
  senderName: string;
  senderPhotoUrl?: string;
  receiverId?: string;
  content: string;
  createdAt: string;
  isOwn: boolean;
  deliveryStatus: DeliveryStatus;
  attachmentUrl?: string | null;
  messageType?: string;
}

export interface ChatToast {
  id: string;
  title: string;
  message: string;
}

export interface ChatState {
  conversations: ChatConversation[];
  messagesByConversation: Record<string, ChatMessage[]>;
  unreadCounts: Record<string, number>;
  onlineUsers: Record<string, boolean>;
  typingUsers: Record<string, Record<string, string>>;
  activeChatId: string | null;
  connectionStatus: ConnectionStatus;
  notifications: ChatToast[];
  isConversationLoading: boolean;
  isMessageLoading: boolean;
}

const initialState: ChatState = {
  conversations: [],
  messagesByConversation: {},
  unreadCounts: {},
  onlineUsers: {},
  typingUsers: {},
  activeChatId: null,
  connectionStatus: 'disconnected',
  notifications: [],
  isConversationLoading: false,
  isMessageLoading: false,
};

function sortConversations(conversations: ChatConversation[]) {
  return [...conversations].sort((a, b) => {
    const aTime = a.lastMessageAt ? new Date(a.lastMessageAt).getTime() : 0;
    const bTime = b.lastMessageAt ? new Date(b.lastMessageAt).getTime() : 0;
    return bTime - aTime;
  });
}

const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    setConversations(state, action: PayloadAction<ChatConversation[]>) {
      state.conversations = sortConversations(action.payload);
      state.unreadCounts = action.payload.reduce<Record<string, number>>((acc, conversation) => {
        acc[conversation.id] = conversation.unreadCount;
        return acc;
      }, {});
    },
    upsertConversation(state, action: PayloadAction<ChatConversation>) {
      const index = state.conversations.findIndex((conversation) => conversation.id === action.payload.id);
      if (index >= 0) {
        state.conversations[index] = { ...state.conversations[index], ...action.payload };
      } else {
        state.conversations.push(action.payload);
      }
      state.conversations = sortConversations(state.conversations);
      state.unreadCounts[action.payload.id] = action.payload.unreadCount;
    },
    setMessages(
      state,
      action: PayloadAction<{ chatId: string; messages: ChatMessage[]; prepend?: boolean }>
    ) {
      const current = state.messagesByConversation[action.payload.chatId] ?? [];
      const merged = action.payload.prepend ? [...action.payload.messages, ...current] : action.payload.messages;
      const byId = new Map<string, ChatMessage>();
      merged.forEach((message) => byId.set(message.id, message));
      state.messagesByConversation[action.payload.chatId] = Array.from(byId.values()).sort(
        (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      );
    },
    addMessage(state, action: PayloadAction<{ chatId: string; message: ChatMessage; incrementUnread?: boolean }>) {
      const messages = state.messagesByConversation[action.payload.chatId] ?? [];
      if (!messages.some((message) => message.id === action.payload.message.id)) {
        messages.push(action.payload.message);
      }
      state.messagesByConversation[action.payload.chatId] = messages.sort(
        (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      );

      const conversation = state.conversations.find((item) => item.id === action.payload.chatId);
      if (conversation) {
        conversation.lastMessage = action.payload.message.content;
        conversation.lastMessageAt = action.payload.message.createdAt;
        if (action.payload.incrementUnread) {
          conversation.unreadCount += 1;
          state.unreadCounts[action.payload.chatId] = conversation.unreadCount;
        }
      }
      state.conversations = sortConversations(state.conversations);
    },
    replacePendingMessage(
      state,
      action: PayloadAction<{ chatId: string; tempId: string; message: ChatMessage }>
    ) {
      const messages = state.messagesByConversation[action.payload.chatId] ?? [];
      state.messagesByConversation[action.payload.chatId] = messages.map((message) =>
        message.id === action.payload.tempId ? action.payload.message : message
      );
    },
    updateMessageStatus(
      state,
      action: PayloadAction<{ messageIds: Array<string | number>; status: DeliveryStatus }>
    ) {
      const targetIds = action.payload.messageIds.map(String);
      Object.keys(state.messagesByConversation).forEach((chatId) => {
        state.messagesByConversation[chatId] = state.messagesByConversation[chatId].map((message) =>
          targetIds.includes(String(message.messageId ?? message.id))
            ? { ...message, deliveryStatus: action.payload.status }
            : message
        );
      });
    },
    setActiveChat(state, action: PayloadAction<string | null>) {
      state.activeChatId = action.payload;
      if (action.payload) {
        state.unreadCounts[action.payload] = 0;
        const conversation = state.conversations.find((item) => item.id === action.payload);
        if (conversation) {
          conversation.unreadCount = 0;
        }
      }
    },
    setOnlineStatus(state, action: PayloadAction<{ userId: string; isOnline: boolean }>) {
      state.onlineUsers[action.payload.userId] = action.payload.isOnline;
      state.conversations = state.conversations.map((conversation) =>
        conversation.receiverId === action.payload.userId
          ? { ...conversation, isOnline: action.payload.isOnline }
          : conversation
      );
    },
    setTypingUser(
      state,
      action: PayloadAction<{ chatId: string; userId: string; name: string; isTyping: boolean }>
    ) {
      state.typingUsers[action.payload.chatId] ??= {};
      if (action.payload.isTyping) {
        state.typingUsers[action.payload.chatId][action.payload.userId] = action.payload.name;
      } else {
        delete state.typingUsers[action.payload.chatId][action.payload.userId];
      }
    },
    setConnectionStatus(state, action: PayloadAction<ConnectionStatus>) {
      state.connectionStatus = action.payload;
    },
    addChatNotification(state, action: PayloadAction<ChatToast>) {
      state.notifications.push(action.payload);
    },
    dismissChatNotification(state, action: PayloadAction<string>) {
      state.notifications = state.notifications.filter((notification) => notification.id !== action.payload);
    },
    setConversationLoading(state, action: PayloadAction<boolean>) {
      state.isConversationLoading = action.payload;
    },
    setMessageLoading(state, action: PayloadAction<boolean>) {
      state.isMessageLoading = action.payload;
    },
  },
});

export const {
  addChatNotification,
  addMessage,
  dismissChatNotification,
  replacePendingMessage,
  setActiveChat,
  setConnectionStatus,
  setConversationLoading,
  setConversations,
  setMessageLoading,
  setMessages,
  setOnlineStatus,
  setTypingUser,
  updateMessageStatus,
  upsertConversation,
} = chatSlice.actions;

export default chatSlice.reducer;
